// Single axios instance + interceptors, mirroring global_100seconds's
// config/Request.jsx: attach the bearer token, single-flight 401 refresh +
// retry, and surface backend errors via toasts. withCredentials is required
// so the browser sends the HttpOnly refresh cookie on /auth/refresh.
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { API_URLS, BASE_URL, ROUTES } from "../common/constants";
import { Auth } from "./Auth";

declare module "axios" {
  // Our two per-request flags: _bypass opts out of refresh handling (used by
  // the auth calls themselves); _retry guards against infinite refresh loops.
  export interface AxiosRequestConfig {
    _bypass?: boolean;
    _retry?: boolean;
  }
}

export const Request = axios.create({ baseURL: BASE_URL, withCredentials: true });

Request.interceptors.request.use((config) => {
  const token = Auth.getAccessToken();
  config.headers.set("Authorization", token ? `Bearer ${token}` : "");
  return config;
});

let refreshPromise: Promise<{ data: { access_token: string; expires_in: number; admin: unknown } }> | null = null;

Request.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config;
    if (!config || config._bypass) return Promise.reject(error);

    const status = error.response?.status;
    const data = error.response?.data as { detail?: string; message?: string } | undefined;
    const message = data?.detail || data?.message;

    if (!error.response) {
      toast.error("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    if (status === 401 && !config._retry) {
      try {
        if (!refreshPromise) {
          refreshPromise = Request.post(API_URLS.REFRESH, null, { _bypass: true }).finally(() => {
            refreshPromise = null;
          });
        }
        const res = await refreshPromise;
        Auth.setSession(res.data.access_token, res.data.expires_in, res.data.admin as never);
        window.dispatchEvent(new CustomEvent("auth:token-refreshed", { detail: res.data }));
        config._retry = true;
        config.headers?.set("Authorization", `Bearer ${res.data.access_token}`);
        return Request(config);
      } catch {
        Auth.clearSession();
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
        if (window.location.pathname !== ROUTES.LOGIN) window.location.href = ROUTES.LOGIN;
        return Promise.reject(error);
      }
    }

    if (status === 403) toast.error(message || "You do not have permission to perform this action.");
    else if (status && status >= 500) toast.error(message || "A server error occurred. Please try again.");
    return Promise.reject(error);
  },
);
